import { OnlineItemGet } from "../../typings/online";
import { paymentT } from "../../typings/payment";
import { getPaymentStatus, isDelivery, isDeposit, isDepositPaid, isLink } from "../../utils/payment";
import "./resource";
import { Enums } from "../../thor.enums";

class PaymentService {
    public static $inject: string[] = ["$log", "$q", "$translate", "localeNumber", "paymentResource"];

    private $t;

    constructor(
        private $log: ng.ILogService,
        private $q: ng.IQService,
        $translate: ng.translate.ITranslateService,
        private localeNumber,
        private paymentResource
    ) {
        this.$t = $translate.instant;
    }

    public get(values: any[] | number) {
        const salesId = (Array.isArray(values) ? values.join(",") : values).toString();
        return this.paymentResource().get({ salesId })
            .$promise
            .then((res: any) => {
                this.$log.info("Fetching payment data ok : ", res.data);
                return res.data;
            })
            .catch(error => {
                this.$log.error("Error while fetching payment", error);
                return this.$q.reject(error);
            });
    }

    public checkItemPaid(
        item: OnlineItemGet,
        items: OnlineItemGet[],
        salePaymentDeposit: paymentT.Payment[] = [],
        isItemGrouped: boolean
    ): boolean {
        const userId = item.pricing?.last_bid?.user_id;

        const itemPayment = (item.payment || [])
            .concat(salePaymentDeposit.filter(p => p.user_id === userId))
            .filter((payment, index, self) => index === self.findIndex(i => i.id === payment.id));

        const itemPaymentAmountTotal = this.localeNumber.sumNbrs(
            itemPayment
                .filter(p => !isDelivery(p) && getPaymentStatus(p) === paymentT.LinkPaymentStatus.SUCCESS)
                .map(p => Number(p.amount))
        );

        const itemFinalSoldPrice = this.localeNumber.roundNbr(
            isItemGrouped
                ? this.localeNumber.sumNbrs(
                      items
                          .filter(i => i.pricing?.last_bid?.user_id === userId && !!i.pricing?.last_bid?.is_winner)
                          .map(i => i.finalSoldPrice)
                  )
                : item.finalSoldPrice
        );
        return itemPaymentAmountTotal >= itemFinalSoldPrice;
    }

    public getLinkPaymentStates(
        payments: paymentT.Payment[],
        currency: Enums.Currency
    ): paymentT.thor.LinkPaymentState[] {
        const linkPaymentStates: paymentT.thor.LinkPaymentState[] = [];

        payments
            .filter(payment => isLink(payment))
            .forEach((payment: paymentT.Payment) => {
                const {
                    amount: paymentAmount,
                    link_payment: { id, status, created, expiration_date, relaunched_dates: relaunchedDates }
                } = payment;

                const amount: number = Number(paymentAmount);
                const type = this.getLinkPaymentStateType(payment);
                const label = this.getLinkPaymentStateLabel({ type, status, amount, currency });

                linkPaymentStates.push({
                    id,
                    label,
                    type,
                    amount,
                    status,
                    created,
                    expiration_date,
                    last_relaunched_date: relaunchedDates ? relaunchedDates[relaunchedDates.length - 1] : undefined
                });
            });

        return linkPaymentStates;
    }

    public getDepositStateLabel(payments: paymentT.Payment[]): string {
        return this.$t(isDepositPaid(payments) ? "encaissee_paren" : "non_encaissee_paren");
    }

    private getLinkPaymentStateLabel(data: any): string {
        let label: string;

        switch (data.status) {
            case paymentT.LinkPaymentStatus.PENDING:
                label = this.$t(
                    data.type === paymentT.thor.LinkPaymentStateType.DELIVERY
                        ? "payment_link_pending.delivery"
                        : "payment_link_pending.item"
                );
                break;
            case paymentT.LinkPaymentStatus.SUCCESS:
                label = this.$t(
                    data.type === paymentT.thor.LinkPaymentStateType.DELIVERY
                        ? "payment_link_success.delivery"
                        : "payment_link_success.item"
                );
                break;
        }
        return `${label} (${this.localeNumber.formatCurrency(data.amount, data.currency)})`;
    }

    private getLinkPaymentStateType(payment: paymentT.Payment): paymentT.thor.LinkPaymentStateType {
        if (isDelivery(payment)) {
            return paymentT.thor.LinkPaymentStateType.DELIVERY;
        }

        if (isDeposit(payment)) {
            return paymentT.thor.LinkPaymentStateType.DEPOSIT;
        }

        return paymentT.thor.LinkPaymentStateType.ITEM;
    }
}

angular
    .module("thor.core.payment.service", ["thor.core.payment.resource"])
    .service("paymentService", PaymentService);
